import { c as _c } from "react/compiler-runtime";
import { getCompanyContext } from "@api/core-api-utils";
import { CompanyIdProvider } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useLoadingSpinner } from "@context-providers/loading-spinner-provider";
import { isLocalhost } from "@faro-lotv/foundation";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useRouteMatch } from "@hooks/use-route-match";
import { useToast } from "@flat-ui";
import { useTrackPageLoad } from "@hooks/use-track-page-load";
import { useTrackTokenMetrics } from "@hooks/use-track-token-metrics";
import { useWorkspaceParams } from "@hooks/workspaces/use-workspace-params";
import { ForbiddenAccessPage } from "@pages/workspace/forbidden-access-page";
import { SelectWorkspacePage } from "@pages/workspace/select-workspace-page";
import { AuthenticatedRoute } from "@router/authenticated-route";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { HttpUtils } from "@stellar/utils";
import { appStateSelector } from "@store/app/app-selector";
import { setSelectedProjectId } from "@store/projects/projects-slice";
import { sdbCompaniesSelector, selectedSdbCompanyIdSelector, hasCoreProductSelector, fetchingSdbCompanyFlagsSelector, availableFeaturesInCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { setSelectedSdbCompanyId } from "@store/sdb-company/sdb-company-slice";
import { fetchCompaniesAndWorkspaces, fetchCompanyCommunicationSettings, fetchCompanyContext, fetchCompanySubscriptions } from "@store/sdb-company/sdb-company-slice-thunk";
import { fetchCompanySettings } from "@store/settings/settings-thunks";
import { COMPANY_SUBSCRIPTIONS_QUERY_OPTIONS } from "@src/constants/subscription-constants";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { SubscriptionExpiredEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useRef, useState } from "react";

/** Company settings that are fetched on initial load */
const COMPANY_SETTINGS_TO_FETCH = ["waypoints_custom_color"];

/**
 * Hooks that loads sdbCompanies (workspaces/companies) and redirect to main app page from root
 */
export function SdbCompanyLoader(t0) {
  const $ = _c(70);
  const {
    children
  } = t0;
  useTrackPageLoad();
  useTrackTokenMetrics();
  const {
    trackEvent
  } = useTrackEvent();
  const appParams = useAppParams();
  const selectedSdbCompanyId = useAppSelector(selectedSdbCompanyIdSelector);
  const sdbCompanies = useAppSelector(sdbCompaniesSelector);
  const {
    isDevModeEnabled
  } = useAppSelector(appStateSelector);
  const availableFeaturesInCompany = useAppSelector(availableFeaturesInCompanySelector);
  const hasCoreProduct = useAppSelector(hasCoreProductSelector);
  const subscriptionExpiredEventWorkspaceId = useRef(null);
  const routeIdentifier = useRouteMatch();
  const lastToastShownForWorkspaceId = useRef(null);
  const dispatch = useAppDispatch();
  const {
    navigateToProjects,
    navigateToSubscriptionExpiredPage
  } = useAppNavigation();
  const {
    setLoadingSpinner,
    isLoadingSpinnerShowing
  } = useLoadingSpinner();
  const {
    openToast
  } = useToast();
  useWorkspaceParams();
  const {
    canViewSubscriptionDetails
  } = useHasUserValidRoleCompanyLevel();
  const [shouldShowSelectWorkspaceMenu, setShouldShowSelectWorkspaceMenu] = useState(false);
  const [shouldShowNoWorkspaceAccessPage, setShouldShowNoWorkspaceAccessPage] = useState(false);
  const [hasFetchedCompaniesAndWorkspaces, setHasFetchedCompaniesAndWorkspaces] = useState(false);
  const [deniedWorkspaceId, setDeniedWorkspaceId] = useState(null);
  const {
    fetchSelectedCompanyContext
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  const {
    handleErrorWithPage
  } = useErrorContext();
  let t1;
  let t2;
  if ($[0] !== dispatch) {
    t1 = () => {
      const fetchCompaniesAndWorkspacesViaStore = async function fetchCompaniesAndWorkspacesViaStore() {
        await dispatch(fetchCompaniesAndWorkspaces()).unwrap();
        setHasFetchedCompaniesAndWorkspaces(true);
      };
      fetchCompaniesAndWorkspacesViaStore();
    };
    t2 = [dispatch];
    $[0] = dispatch;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== isLoadingSpinnerShowing || $[4] !== setLoadingSpinner || $[5] !== shouldShowSelectWorkspaceMenu) {
    t3 = () => {
      if (isLoadingSpinnerShowing && (!globalThis.location.pathname.includes("data-management") || shouldShowSelectWorkspaceMenu)) {
        setLoadingSpinner(false);
      }
    };
    t4 = [isLoadingSpinnerShowing, setLoadingSpinner, shouldShowSelectWorkspaceMenu];
    $[3] = isLoadingSpinnerShowing;
    $[4] = setLoadingSpinner;
    $[5] = shouldShowSelectWorkspaceMenu;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== canViewSubscriptionDetails || $[9] !== dispatch || $[10] !== selectedSdbCompanyId) {
    t5 = () => {
      const fetchCompanyContextViaStore = async function fetchCompanyContextViaStore() {
        await dispatch(fetchCompanyContext());
      };
      const fetchBatchCompanySettingsViaStore = async function fetchBatchCompanySettingsViaStore(companyId) {
        await dispatch(fetchCompanySettings({
          identifiers: [...COMPANY_SETTINGS_TO_FETCH],
          companyId
        }));
      };
      const fetchCompanyCommunicationSettingsViaStore = async function fetchCompanyCommunicationSettingsViaStore() {
        await dispatch(fetchCompanyCommunicationSettings());
      };
      const fetchSubscriptionViaStore = function fetchSubscriptionViaStore() {
        if (!canViewSubscriptionDetails) {
          return;
        }
        dispatch(fetchCompanySubscriptions({
          queryOptions: COMPANY_SUBSCRIPTIONS_QUERY_OPTIONS
        }));
      };
      if (selectedSdbCompanyId) {
        fetchCompanyContextViaStore();
        fetchBatchCompanySettingsViaStore(selectedSdbCompanyId);
        fetchCompanyCommunicationSettingsViaStore();
        fetchSubscriptionViaStore();
      }
    };
    t6 = [canViewSubscriptionDetails, dispatch, selectedSdbCompanyId];
    $[8] = canViewSubscriptionDetails;
    $[9] = dispatch;
    $[10] = selectedSdbCompanyId;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== navigateToProjects) {
    t7 = firstCompany => {
      if (firstCompany && !isLocalhost() && firstCompany.url.includes(runtimeConfig.urls.hbDashboardUrl)) {
        window.location.href = firstCompany.url;
      } else {
        navigateToProjects({
          companyId: firstCompany.id
        });
      }
    };
    $[13] = navigateToProjects;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const navigateToFirstCompany = t7;
  let t8;
  if ($[15] !== dispatch || $[16] !== handleErrorWithPage || $[17] !== hasFetchedCompaniesAndWorkspaces || $[18] !== isDevModeEnabled || $[19] !== sdbCompanies || $[20] !== selectedSdbCompanyId) {
    t8 = async companyIdFromParams => {
      if (lastToastShownForWorkspaceId.current === companyIdFromParams) {
        return;
      }
      if (!hasFetchedCompaniesAndWorkspaces) {
        return;
      }
      const hasAccessToWorkspace = sdbCompanies.some(company => company.id === companyIdFromParams);
      if (!hasAccessToWorkspace) {
        setDeniedWorkspaceId(companyIdFromParams);
        return;
      }
      ;
      try {
        if (companyIdFromParams !== selectedSdbCompanyId) {
          await getCompanyContext(companyIdFromParams, isDevModeEnabled);
          dispatch(setSelectedSdbCompanyId(companyIdFromParams));
        }
        setShouldShowSelectWorkspaceMenu(false);
        setDeniedWorkspaceId(null);
        lastToastShownForWorkspaceId.current = null;
      } catch (t9) {
        const error = t9;
        const errorStatus = HttpUtils.getErrorStatus(error);
        if (errorStatus && (errorStatus === HttpUtils.HTTP_ERROR_CODE.NotFound || errorStatus === HttpUtils.HTTP_ERROR_CODE.Forbidden)) {
          setDeniedWorkspaceId(companyIdFromParams);
        } else {
          handleErrorWithPage({
            id: `getCompanyContext-${Date.now().toString()}`,
            title: "Failed to fetch the company. Please reload the page to try again.",
            error
          });
        }
      }
    };
    $[15] = dispatch;
    $[16] = handleErrorWithPage;
    $[17] = hasFetchedCompaniesAndWorkspaces;
    $[18] = isDevModeEnabled;
    $[19] = sdbCompanies;
    $[20] = selectedSdbCompanyId;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleCompanyNavigation = t8;
  let t9;
  if ($[22] !== navigateToFirstCompany || $[23] !== sdbCompanies[0] || $[24] !== sdbCompanies.length) {
    t9 = () => {
      sdbCompanies.length === 1 ? navigateToFirstCompany(sdbCompanies[0]) : setShouldShowSelectWorkspaceMenu(true);
    };
    $[22] = navigateToFirstCompany;
    $[23] = sdbCompanies[0];
    $[24] = sdbCompanies.length;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const redirectToAvailableWorkspace = t9;
  let t10;
  let t11;
  if ($[26] !== appParams.companyId || $[27] !== handleCompanyNavigation || $[28] !== hasFetchedCompaniesAndWorkspaces) {
    t10 = () => {
      if (appParams.companyId && hasFetchedCompaniesAndWorkspaces) {
        handleCompanyNavigation(appParams.companyId);
      }
    };
    t11 = [appParams.companyId, hasFetchedCompaniesAndWorkspaces, handleCompanyNavigation];
    $[26] = appParams.companyId;
    $[27] = handleCompanyNavigation;
    $[28] = hasFetchedCompaniesAndWorkspaces;
    $[29] = t10;
    $[30] = t11;
  } else {
    t10 = $[29];
    t11 = $[30];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[31] !== deniedWorkspaceId || $[32] !== hasFetchedCompaniesAndWorkspaces || $[33] !== openToast || $[34] !== redirectToAvailableWorkspace || $[35] !== sdbCompanies.length) {
    t12 = () => {
      if (!deniedWorkspaceId || !hasFetchedCompaniesAndWorkspaces) {
        return;
      }
      if (lastToastShownForWorkspaceId.current === deniedWorkspaceId) {
        return;
      }
      const workspaceId = deniedWorkspaceId;
      lastToastShownForWorkspaceId.current = workspaceId;
      setDeniedWorkspaceId(null);
      if (sdbCompanies.length === 0) {
        setShouldShowNoWorkspaceAccessPage(true);
        return;
      }
      const redirectMessage = sdbCompanies.length === 1 ? "You have been redirected to your workspace." : "Please select a different workspace or contact the workspace administrator.";
      openToast({
        title: "Access denied",
        message: <>You don't have access to workspace <var>{workspaceId}</var>.{" "}{redirectMessage}</>,
        variant: "error"
      });
      redirectToAvailableWorkspace();
    };
    t13 = [sdbCompanies.length, deniedWorkspaceId, hasFetchedCompaniesAndWorkspaces, redirectToAvailableWorkspace, openToast];
    $[31] = deniedWorkspaceId;
    $[32] = hasFetchedCompaniesAndWorkspaces;
    $[33] = openToast;
    $[34] = redirectToAvailableWorkspace;
    $[35] = sdbCompanies.length;
    $[36] = t12;
    $[37] = t13;
  } else {
    t12 = $[36];
    t13 = $[37];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[38] !== appParams.companyId || $[39] !== hasFetchedCompaniesAndWorkspaces || $[40] !== redirectToAvailableWorkspace) {
    t14 = () => {
      if (!appParams.companyId && hasFetchedCompaniesAndWorkspaces) {
        redirectToAvailableWorkspace();
      }
    };
    t15 = [appParams.companyId, hasFetchedCompaniesAndWorkspaces, redirectToAvailableWorkspace];
    $[38] = appParams.companyId;
    $[39] = hasFetchedCompaniesAndWorkspaces;
    $[40] = redirectToAvailableWorkspace;
    $[41] = t14;
    $[42] = t15;
  } else {
    t14 = $[41];
    t15 = $[42];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[43] !== appParams.projectId || $[44] !== dispatch) {
    t16 = () => {
      dispatch(setSelectedProjectId(appParams.projectId ?? null));
    };
    t17 = [appParams.projectId, dispatch];
    $[43] = appParams.projectId;
    $[44] = dispatch;
    $[45] = t16;
    $[46] = t17;
  } else {
    t16 = $[45];
    t17 = $[46];
  }
  useEffect(t16, t17);
  const isOnSubscriptionExpiredPage = routeIdentifier === "subscriptionExpired";
  let t18;
  let t19;
  if ($[47] !== appParams.companyId || $[48] !== availableFeaturesInCompany || $[49] !== fetchSelectedCompanyContext || $[50] !== hasCoreProduct || $[51] !== isOnSubscriptionExpiredPage || $[52] !== navigateToSubscriptionExpiredPage || $[53] !== selectedSdbCompanyId || $[54] !== trackEvent) {
    t18 = () => {
      const hasFetchFailed = fetchSelectedCompanyContext === "rejected";
      const hasFetchSucceededButCoreProductDisabled = fetchSelectedCompanyContext === "succeeded" && !hasCoreProduct;
      if ((hasFetchFailed || hasFetchSucceededButCoreProductDisabled) && selectedSdbCompanyId && appParams.companyId && !isOnSubscriptionExpiredPage) {
        if (subscriptionExpiredEventWorkspaceId.current !== selectedSdbCompanyId) {
          subscriptionExpiredEventWorkspaceId.current = selectedSdbCompanyId;
          trackEvent({
            name: SubscriptionExpiredEvents.subscriptionExpired,
            props: {
              availableFeatures: availableFeaturesInCompany ?? [],
              fetchSelectedCompanyContext
            }
          });
        }
        navigateToSubscriptionExpiredPage({
          companyId: selectedSdbCompanyId
        });
      }
    };
    t19 = [appParams.companyId, availableFeaturesInCompany, fetchSelectedCompanyContext, hasCoreProduct, isOnSubscriptionExpiredPage, navigateToSubscriptionExpiredPage, selectedSdbCompanyId, trackEvent];
    $[47] = appParams.companyId;
    $[48] = availableFeaturesInCompany;
    $[49] = fetchSelectedCompanyContext;
    $[50] = hasCoreProduct;
    $[51] = isOnSubscriptionExpiredPage;
    $[52] = navigateToSubscriptionExpiredPage;
    $[53] = selectedSdbCompanyId;
    $[54] = trackEvent;
    $[55] = t18;
    $[56] = t19;
  } else {
    t18 = $[55];
    t19 = $[56];
  }
  useEffect(t18, t19);
  const isContextReady = fetchSelectedCompanyContext === "succeeded" || fetchSelectedCompanyContext === "rejected";
  const hasActiveSubscription = isContextReady && hasCoreProduct;
  let t20;
  if ($[57] !== shouldShowNoWorkspaceAccessPage) {
    t20 = shouldShowNoWorkspaceAccessPage && <AuthenticatedRoute><ForbiddenAccessPage /></AuthenticatedRoute>;
    $[57] = shouldShowNoWorkspaceAccessPage;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== shouldShowSelectWorkspaceMenu) {
    t21 = shouldShowSelectWorkspaceMenu && <AuthenticatedRoute><SelectWorkspacePage /></AuthenticatedRoute>;
    $[59] = shouldShowSelectWorkspaceMenu;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== children || $[62] !== hasActiveSubscription || $[63] !== isOnSubscriptionExpiredPage || $[64] !== selectedSdbCompanyId) {
    t22 = selectedSdbCompanyId && (hasActiveSubscription || isOnSubscriptionExpiredPage) && <CompanyIdProvider companyId={selectedSdbCompanyId}>{children}</CompanyIdProvider>;
    $[61] = children;
    $[62] = hasActiveSubscription;
    $[63] = isOnSubscriptionExpiredPage;
    $[64] = selectedSdbCompanyId;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== t20 || $[67] !== t21 || $[68] !== t22) {
    t23 = <>{t20}{t21}{t22}</>;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  return t23;
}